import { useState } from "react";

import { Button, Icon, TextArea } from "@smart/itops-sb-design-system-dom";
import {
  useToggleSlug,
  useUpdateAIUserFeedback,
} from "@smart/manage-gql-client-dom";

import { GqlForm } from "../types";

const MAX_RATING = 5;
const stars = Array.from({ length: MAX_RATING }, (_, index) => index + 1);

export type GeneratingFeedbackProps = {
  aiUserFeedback?: GqlForm["aiUserFeedback"] | null;
  title: string;
  updating?: boolean;
  updateAIUserFeedback: ReturnType<typeof useUpdateAIUserFeedback>[0];
  updateSlug: ReturnType<typeof useToggleSlug>[0];
  form?: GqlForm | null;
};

export const GeneratingFeedback = ({
  aiUserFeedback,
  title,
  updating,
  updateAIUserFeedback,
  updateSlug,
  form,
}: GeneratingFeedbackProps) => {
  const [retry, setRetry] = useState(false);
  const [rating, setRating] = useState<number>();
  const [hoveredRating, setHoveredRating] = useState<number>();
  const [feedback, setFeedback] = useState<string>("");

  switch (aiUserFeedback?.status) {
    case "generated":
      return retry ? (
        <>
          <h4 className="text-paragraph my-3">
            How could this form be improved?
          </h4>
          <div className="mb-4">
            <TextArea
              id="feedback"
              value={feedback}
              rows={3}
              onChange={(e) => setFeedback(e.currentTarget.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              text="Cancel"
              variant="secondaryDefault"
              onClick={() => setRetry(false)}
            />
            <Button
              text="Regenerate"
              variant="primaryDefault"
              onClick={async () => {
                await updateAIUserFeedback({
                  variables: {
                    uri: aiUserFeedback.uri,
                    status: "generating",
                    statusText: "",
                    feedback,
                  },
                });
                setRetry(false);
                setFeedback("");
              }}
            />
          </div>
        </>
      ) : (
        <>
          <h4 className="text-paragraph my-3">
            How would you like to continue?
          </h4>
          <div className="flex justify-start gap-2">
            <Button
              text="Refine"
              size="default"
              leftIcon="regularArrowsRotate"
              variant="secondaryDefault"
              disabled={updating}
              onClick={() => setRetry(true)}
            />
            <Button
              text="Use"
              leftIcon="regularCheck"
              variant="primaryDefault"
              disabled={updating}
              onClick={async () => {
                await updateAIUserFeedback({
                  variables: {
                    uri: aiUserFeedback.uri,
                    status: "completed",
                    statusText: `Saving "${title}"...`,
                  },
                });

                if (form?.rawForm.values.matterId) {
                  await updateSlug({
                    variables: {
                      formUri: form.uri,
                      fields: { active: true },
                    },
                  });
                }
              }}
            />
          </div>
        </>
      );
    case "failed":
      return (
        <>
          <h4 className="text-paragraph my-3 text-red-600">
            Something went wrong while generating this form. Please try again.
          </h4>
          <div className="flex justify-start gap-2">
            <Button
              text="Try again"
              size="default"
              leftIcon="regularArrowsRotate"
              variant="primaryDefault"
              disabled={updating}
              onClick={async () => {
                await updateAIUserFeedback({
                  variables: {
                    uri: aiUserFeedback!.uri,
                    status: "generating",
                    statusText: "",
                  },
                });
              }}
            />
          </div>
        </>
      );
    case "completed":
      return (
        <>
          <Button
            className="absolute top-4 right-4"
            rightIcon="regularXmark"
            aria-label="Close Modal - X"
            variant="inlineSubtle"
            onClick={() =>
              updateAIUserFeedback({
                variables: { uri: aiUserFeedback.uri, status: "reviewed" },
              })
            }
          />
          <h4 className="text-paragraph my-3">
            How would you rate this experience?
          </h4>
          <div className="flex justify-start flex-row flex-nowrap my-4">
            {stars.map((star) => {
              const isSelected = rating && star <= rating;
              const isHovered = hoveredRating && star <= hoveredRating;
              const shouldShowSolid = isSelected || isHovered;

              return (
                <button
                  key={star}
                  type="button"
                  className="flex items-center justify-center cursor-pointer p-1.5 group"
                  onClick={() => setRating(star)}
                  onMouseEnter={() => setHoveredRating(star)}
                  onMouseLeave={() => setHoveredRating(undefined)}
                  aria-pressed={!!isSelected}
                >
                  <Icon
                    name={shouldShowSolid ? "solidStar" : "regularStar"}
                    className="text-orange-500 w-12 h-12"
                  />
                </button>
              );
            })}
          </div>
          {rating && (
            <>
              <h4 className="text-paragraph my-3">
                {rating < 4
                  ? "How could we improve this experience?"
                  : "What went well with this experience?"}
              </h4>
              <div className="mb-4">
                <TextArea
                  id="feedback"
                  value={feedback}
                  rows={3}
                  onChange={(e) => setFeedback(e.currentTarget.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  text="Send feedback"
                  variant="primaryDefault"
                  onClick={() =>
                    updateAIUserFeedback({
                      variables: {
                        uri: aiUserFeedback.uri,
                        status: "reviewed",
                        statusText: "",
                        rating,
                        feedback,
                      },
                    })
                  }
                />
              </div>
            </>
          )}
        </>
      );
    default:
      return null;
  }
};
